import { Button, ButtonGroup, Heading, IconHelp, ModalDialog, TextButton, Trigger } from '@zillow/constellation';
import { SubmitRequestPageComponent } from './SubmitRequestPage';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { RentalManagerAuthCheckUserInfoDto } from '@zg-rentals/ts-rental-manager-api-client';
import { StyledFloatingButtonDefault, StyledFloatingButtonPropertiesPage } from './StyledComponents';

// Request Widget will open automatically for urls where `?${SUPPORT_PARAM}=true'
const SUPPORT_PARAM = 'support';

interface TriggeredProps {
  isOpen: boolean;
  onClose: boolean;
}

interface SubmitRequestModalPageFooterProps {
  onClick: () => void;
  disabled: boolean;
  onCancel?: () => void;
  customSubmitText?: string | undefined;
}

export const SubmitRequestModalPageFooter = ({
  onClick,
  disabled,
  onCancel,
  customSubmitText,
}: SubmitRequestModalPageFooterProps) => (
  <ButtonGroup aria-label="Modal actions">
    {onCancel !== undefined && !customSubmitText && <TextButton onClick={onCancel}>Cancel</TextButton>}
    <Button buttonType="primary" onClick={onClick} disabled={disabled}>
      {customSubmitText ? customSubmitText : 'Submit'}
    </Button>
  </ButtonGroup>
);

export const RequestWidget = ({ zrmwUser }: { zrmwUser?: RentalManagerAuthCheckUserInfoDto }) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [hasClosedWidget, setHasClosedWidget] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const forceOpenWidget = !hasClosedWidget && params.get(SUPPORT_PARAM) === 'true';

  const getPageSpecificStyling = (pathname: string) => {
    const listingPageRegex = new RegExp('/rental-manager/properties/[a-z0-9]*/listing');
    return pathname.match(listingPageRegex) ? StyledFloatingButtonPropertiesPage : StyledFloatingButtonDefault;
  };

  const WidgetButton = getPageSpecificStyling(location.pathname);

  return (
    <Trigger
      triggered={({ isOpen }: TriggeredProps, close: () => void) => {
        const onModalClose = () => {
          setHasClosedWidget(true);
          setSubmitClicked(false);
          close();
        };
        const onNext = !submitClicked ? () => setSubmitClicked(true) : () => onModalClose();
        return (
          <ModalDialog
            isOpen={isOpen || forceOpenWidget}
            onClose={onModalClose}
            size="lg"
            dividers={false}
            renderHeader={<Heading level="6">Support Request</Heading>}
            renderBody={
              <SubmitRequestPageComponent
                zrmwUser={zrmwUser}
                setSubmitClicked={setSubmitClicked}
                submitClicked={submitClicked}
                setCanSubmit={setCanSubmit}
              />
            }
            renderFooter={
              <SubmitRequestModalPageFooter
                onClick={onNext}
                disabled={!canSubmit}
                onCancel={onModalClose}
                customSubmitText={submitClicked ? 'Next' : undefined}
              />
            }
          />
        );
      }}
    >
      <WidgetButton
        size="lg"
        buttonType="secondary"
        icon={<IconHelp />}
        appearance="circle"
        bare={false}
        title="Need Support"
      />
    </Trigger>
  );
};
